/**
 * Entity extractor — ARCHITECTURE.md section 6.3.
 *
 * Uses Sonnet via the LLM adapter to extract structured entities
 * from source text. Returns entities with mandatory provenance fields.
 *
 * Pipeline position: after relevance filter, before deterministic validation.
 *
 * Key behaviors:
 * - Grounding check: extraction_quote must exist verbatim in source text
 * - Entities missing mandatory fields are rejected with logged reason
 * - JSON parsing retried 3x via the adapter (malformed response handling)
 */

import type { EntityType } from '@/intelligence/entities/types';
import type { OntologyExtensions } from '@/config/desk-config';
import type {
  LLMAdapter,
  RawExtractedEntity,
  LLMCallResult,
} from './llm-adapter';

// ---------------------------------------------------------------------------
// Extraction context — metadata from the collection task
// ---------------------------------------------------------------------------

/** Source metadata provided by the collection task, added to extracted entities */
export interface ExtractionContext {
  sourceUrl: string;
  collectorId: string;
  collectedAt: string;  // ISO 8601
  sourceId: string;
}

// ---------------------------------------------------------------------------
// Enriched entity — raw extraction + source metadata
// ---------------------------------------------------------------------------

/** Entity after extraction and enrichment, before deterministic validation */
export interface ExtractedEntityWithProvenance {
  type: EntityType;
  name: string;
  description?: string;
  aliases: string[];
  properties: Record<string, unknown>;
  provenance: {
    sourceUrl: string;
    extractionQuote: string;
    collectorId: string;
    collectedAt: string;
    confidence: number;
  };
  /** Grounding check result — true if extractionQuote was found in source text */
  grounded: boolean;
}

// ---------------------------------------------------------------------------
// Extraction result
// ---------------------------------------------------------------------------

export interface ExtractionResult {
  /** Entities that passed initial checks (grounding, required fields) */
  extracted: ExtractedEntityWithProvenance[];
  /** Entities rejected at extraction time, with reasons */
  rejected: Array<{ entity: RawExtractedEntity; reason: string }>;
  /** Token usage for this extraction call */
  tokensUsed: number;
  /** Model used */
  model: string;
}

// ---------------------------------------------------------------------------
// Entity extractor interface
// ---------------------------------------------------------------------------

export interface EntityExtractor {
  /**
   * Extract entities from source text.
   * @param text - Raw source text to extract from
   * @param context - Source metadata from the collection task
   * @param entityTypes - Allowed entity types (from desk config ontology)
   * @param ontologyExtensions - Optional domain-specific extensions
   */
  extract(
    text: string,
    context: ExtractionContext,
    entityTypes: EntityType[],
    ontologyExtensions?: OntologyExtensions,
  ): Promise<ExtractionResult>;
}

// ---------------------------------------------------------------------------
// Grounding check — verify extractionQuote appears verbatim in source
// ---------------------------------------------------------------------------

/**
 * Check if the extraction quote exists verbatim in the source text.
 * Uses case-insensitive comparison to handle minor formatting differences,
 * but the quote must still be a contiguous substring.
 */
function checkGrounding(quote: string, sourceText: string): boolean {
  if (!quote || quote.length < 3) return false;
  // Normalize whitespace (collapse multiple spaces/newlines) for both
  const normalizeWs = (s: string) => s.replace(/\s+/g, ' ').trim();
  const normalizedQuote = normalizeWs(quote).toLowerCase();
  const normalizedSource = normalizeWs(sourceText).toLowerCase();
  return normalizedSource.includes(normalizedQuote);
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Create an entity extractor for an investigation.
 * @param adapter - LLM adapter instance (with its own token tracker)
 */
export function createEntityExtractor(adapter: LLMAdapter): EntityExtractor {
  return {
    async extract(text, context, entityTypes, ontologyExtensions) {
      // Call LLM via adapter
      const result: LLMCallResult<{ entities: RawExtractedEntity[] }> =
        await adapter.extractEntities(text, entityTypes, ontologyExtensions);

      const rawEntities = result.data.entities ?? [];
      const extracted: ExtractedEntityWithProvenance[] = [];
      const rejected: Array<{ entity: RawExtractedEntity; reason: string }> = [];

      for (const raw of rawEntities) {
        // Check required fields
        if (!raw.name || raw.name.trim().length === 0) {
          rejected.push({ entity: raw, reason: 'Missing required field: name' });
          continue;
        }

        if (!raw.type) {
          rejected.push({ entity: raw, reason: 'Missing required field: type' });
          continue;
        }

        if (!entityTypes.includes(raw.type)) {
          rejected.push({
            entity: raw,
            reason: `Entity type "${raw.type}" not in allowed types: ${entityTypes.join(', ')}`,
          });
          continue;
        }

        if (!raw.extractionQuote || raw.extractionQuote.trim().length === 0) {
          rejected.push({ entity: raw, reason: 'Missing required field: extractionQuote' });
          continue;
        }

        if (typeof raw.confidence !== 'number' || raw.confidence < 0 || raw.confidence > 1) {
          rejected.push({
            entity: raw,
            reason: `Invalid confidence value: ${raw.confidence} (must be 0-1)`,
          });
          continue;
        }

        // Grounding check
        const grounded = checkGrounding(raw.extractionQuote, text);

        if (!grounded) {
          rejected.push({
            entity: raw,
            reason: 'Grounding check failed: extractionQuote not found verbatim in source text',
          });
          continue;
        }

        // Build enriched entity
        extracted.push({
          type: raw.type,
          name: raw.name.trim(),
          description: raw.description ?? undefined,
          aliases: raw.aliases ?? [],
          properties: raw.properties ?? {},
          provenance: {
            sourceUrl: context.sourceUrl,
            extractionQuote: raw.extractionQuote,
            collectorId: context.collectorId,
            collectedAt: context.collectedAt,
            confidence: raw.confidence,
          },
          grounded: true,
        });
      }

      return {
        extracted,
        rejected,
        tokensUsed: result.tokensUsed,
        model: result.model,
      };
    },
  };
}
